import {
    Button, Menu, MenuButton, MenuList, VStack, Text,
} from '@chakra-ui/react';

import { ReportedFindingDataMachine, ReportedFindingStatus } from '@acp/shared';
import { FaIcon, ThemeTokens } from '@acp/core-ui';
import { faChevronDown } from '@fortawesome/sharp-light-svg-icons';
import { DismissFindingButton } from '../../../../property-details/reported-findings-tab/dismiss-reported-finding-button';
import { RequestQuoteButton } from '../components/request-quote-wizard';
import { ResolveByThirdParty } from '../components/resolve-by-third-party';
import { ReportedFindingsWizardTable } from '../components/table/reported-findings-wizard-table';
import { ForwardQuoteButton } from './forward-quote-wizard-screen';
import { ReviewQuoteButton } from '../components/review-quote-button';
import { InformDIButton } from '../components/inform-di-wizard';
import { EnterBuiltYearButton } from '../components/enter-built-year';
import { ReportedFindingsMachineContext } from '../machine/context';

function DropDownButton({
    reportedFinding,
}: {
    reportedFinding: ReportedFindingDataMachine;
}) {
    const { quotationId } = reportedFinding.quotation;
    const { thirdPartyInvalidStatus } = reportedFinding.problemRegister;

    const canResolveViaThirdParty = (thirdPartyInvalidStatus === false || quotationId === null)
        && reportedFinding.problemRegister.status !== ReportedFindingStatus.REVIEW_REQUIRED
        && reportedFinding.problemRegister.status !== ReportedFindingStatus.RESOLVED;

    const canRequestQuote = reportedFinding.problemRegister.canRequestQuote === true;
    const canForwardToLandlord = reportedFinding.problemRegister.canForwardToLandlord === true;
    const canEnterBuiltYear = reportedFinding.problemRegister.canEnterBuiltYear === true;
    const canInformDI = reportedFinding.problemRegister.canInformDI === true;

    // Can't do anything = no button
    if (
        !canRequestQuote
        && !canForwardToLandlord
        && !canEnterBuiltYear
        && !canInformDI
        && !canResolveViaThirdParty
    ) {
        return (
            <Button
                borderRightWidth={1}
                borderRightColor="secondary.900"
                borderColor={ThemeTokens.Defaults.borderColor}
                color="muted.700"
                bgColor="white"
                colorScheme="charcoal"
                variant="outline"
                size="sm"
                disabled
            >
                Resolve
            </Button>
        );
    }

    return (
        <Menu size="large">
            <MenuButton
                as={Button}
                borderRightWidth={1}
                borderRightColor="secondary.900"
                borderColor={ThemeTokens.Defaults.borderColor}
                color="muted.700"
                bgColor="white"
                colorScheme="charcoal"
                variant="outline"
                size="sm"
            >
                Resolve
                <FaIcon ml={2} icon={faChevronDown} />
            </MenuButton>
            <MenuList>
                {reportedFinding.problemRegister.canRequestQuote === true && (
                    <RequestQuoteButton reportedFinding={reportedFinding} />
                )}
                {reportedFinding.problemRegister.canForwardToLandlord === true && (
                    <>
                        <ForwardQuoteButton reportedFinding={reportedFinding} />
                        {quotationId != null && (<ReviewQuoteButton quotationId={quotationId} />)}
                    </>
                )}
                {canResolveViaThirdParty && (
                    <ResolveByThirdParty
                        disabled={false}
                        reportedFindingId={reportedFinding.problemRegister?.problemRegisterId}
                    />
                )}

                {reportedFinding.problemRegister.canEnterBuiltYear === true
                     && <EnterBuiltYearButton />}
                {reportedFinding.problemRegister.canInformDI === true
                     && <InformDIButton reportedFinding={reportedFinding} />}

                <DismissFindingButton reportedFinding={reportedFinding} />
            </MenuList>
        </Menu>
    );
}

function ResolveFindingSummaryDescription() {
    return (
        <VStack w="100%" alignItems="stretch" gap={6}>
            <Text fontSize="md" fontWeight="medium">
                Let&apos;s resolve the reported findings
            </Text>
            <Text fontSize="md">
                Click &quot;Resolve&quot; and select an action from the dropdown to proceed.
            </Text>
        </VStack>
    );
}

function ResolveFindingSummaryScreen() {
    const rawFindingResponse = ReportedFindingsMachineContext.useSelector(
        (state) => state.context.apiResponse
    );

    if (rawFindingResponse == null) {
        return null;
    }

    return (
        <VStack align="left" w="100%" gap={0} mb={4}>
            <ResolveFindingSummaryDescription />
            <ReportedFindingsWizardTable>
                {(item) => (
                    <DropDownButton
                        reportedFinding={item}
                    />
                )}
            </ReportedFindingsWizardTable>
        </VStack>
    );
}

export { ResolveFindingSummaryScreen };
